import { useState, useEffect } from "react";
import axios from "axios";
import API_ENDPOINTS from "config/api";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React examples
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import HorizontalBarChart from "examples/Charts/BarCharts/HorizontalBarChart";
import PieChart from "examples/Charts/PieChart";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";

export default function DashboardLogistica() {
  const [menu, setMenu] = useState(null);
  const [periodo, setPeriodo] = useState("mes");

  const openMenu = ({ currentTarget }) => setMenu(currentTarget);
  const closeMenu = () => setMenu(null);

  const handlePeriodoChange = (nuevoPeriodo) => {
    setPeriodo(nuevoPeriodo);
    closeMenu();
  };

  const [kpis, setKpis] = useState({
    entregas: {
      total: 0,
      completadas: 0,
      pendientes: 0,
      retrasadas: 0,
      porcentajeExito: 0,
    },
    vehiculos: {
      total: 0,
      activos: 0,
      mantenimiento: 0,
      inactivos: 0,
      porcentajeDisponibilidad: 0,
    },
    rutas: {
      total: 0,
      enCurso: 0,
      completadas: 0,
      pendientes: 0,
      eficienciaPromedio: 0,
    },
    costos: {
      totalMes: 0,
      combustible: 0,
      mantenimiento: 0,
      salarios: 0,
      costoPromedioPorEntrega: 0,
    },
  });

  const [entregasPorDia, setEntregasPorDia] = useState({
    labels: [],
    datasets: [],
  });

  const [vehiculosPorTipo, setVehiculosPorTipo] = useState({
    labels: [],
    datasets: { label: "Vehículos", backgroundColors: [], data: [] },
  });

  const [eficienciaRutas, setEficienciaRutas] = useState({
    labels: [],
    datasets: [],
  });

  const [costosMensuales, setCostosMensuales] = useState({
    labels: [],
    datasets: [],
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.LOGISTICA.DASHBOARD, {
          params: { periodo },
        });
        setKpis(response.data.kpis || {});
        setEntregasPorDia(response.data.entregasPorDia || { labels: [], datasets: [] });
        setVehiculosPorTipo(
          response.data.vehiculosPorTipo || {
            labels: [],
            datasets: { label: "Vehículos", backgroundColors: [], data: [] },
          }
        );
        setEficienciaRutas(response.data.eficienciaRutas || { labels: [], datasets: [] });
        setCostosMensuales(response.data.costosMensuales || { labels: [], datasets: [] });
      } catch (error) {
        console.error("Error al cargar datos del dashboard:", error);
        // Initialize with empty safe defaults on error
        setKpis({
          entregas: {
            total: 0,
            completadas: 0,
            pendientes: 0,
            retrasadas: 0,
            porcentajeExito: 0,
          },
          vehiculos: {
            total: 0,
            activos: 0,
            mantenimiento: 0,
            inactivos: 0,
            porcentajeDisponibilidad: 0,
          },
          rutas: {
            total: 0,
            enCurso: 0,
            completadas: 0,
            pendientes: 0,
            eficienciaPromedio: 0,
          },
          costos: {
            totalMes: 0,
            combustible: 0,
            mantenimiento: 0,
            salarios: 0,
            costoPromedioPorEntrega: 0,
          },
        });
        setEntregasPorDia({ labels: [], datasets: [] });
        setVehiculosPorTipo({
          labels: [],
          datasets: [{ label: "Vehículos", backgroundColors: [], data: [] }],
        });
        setEficienciaRutas({ labels: [], datasets: [] });
        setCostosMensuales({ labels: [], datasets: [] });
      }
    };
    fetchData();
  }, [periodo]);

  const renderMenu = (
    <Menu
      id="simple-menu"
      anchorEl={menu}
      anchorOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={Boolean(menu)}
      onClose={closeMenu}
    >
      <MenuItem onClick={() => handlePeriodoChange("dia")}>Hoy</MenuItem>
      <MenuItem onClick={() => handlePeriodoChange("semana")}>Esta Semana</MenuItem>
      <MenuItem onClick={() => handlePeriodoChange("mes")}>Este Mes</MenuItem>
      <MenuItem onClick={() => handlePeriodoChange("año")}>Este Año</MenuItem>
    </Menu>
  );

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MDTypography variant="h5" fontWeight="medium">
          Dashboard de Logística -{" "}
          {periodo === "dia"
            ? "Hoy"
            : periodo === "semana"
              ? "Esta Semana"
              : periodo === "mes"
                ? "Este Mes"
                : "Este Año"}
        </MDTypography>
        <MDBox color="text" px={2}>
          <Icon sx={{ cursor: "pointer", fontWeight: "bold" }} fontSize="small" onClick={openMenu}>
            more_vert
          </Icon>
        </MDBox>
        {renderMenu}
      </MDBox>

      {/* KPIs Principales */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6} lg={3}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="success"
              icon="local_shipping"
              title="Entregas Completadas"
              count={kpis?.entregas?.completadas || 0}
              percentage={{
                color: "success",
                amount: `${kpis?.entregas?.porcentajeExito || 0}%`,
                label: "tasa de éxito",
              }}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              icon="pending_actions"
              title="Entregas Pendientes"
              count={kpis?.entregas?.pendientes || 0}
              percentage={{
                color: "warning",
                amount: `${kpis?.entregas?.retrasadas || 0}`,
                label: "retrasadas",
              }}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="info"
              icon="airport_shuttle"
              title="Vehículos Activos"
              count={`${kpis?.vehiculos?.activos || 0}/${kpis?.vehiculos?.total || 0}`}
              percentage={{
                color: "success",
                amount: `${kpis?.vehiculos?.porcentajeDisponibilidad || 0}%`,
                label: "disponibilidad",
              }}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="primary"
              icon="route"
              title="Rutas en Curso"
              count={kpis?.rutas?.enCurso || 0}
              percentage={{
                color: "success",
                amount: `${kpis?.rutas?.eficienciaPromedio || 0}%`,
                label: "eficiencia promedio",
              }}
            />
          </MDBox>
        </Grid>
      </Grid>

      {/* KPIs de Costos */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6} lg={3}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="dark"
              icon="attach_money"
              title="Costo Total"
              count={`$${((kpis?.costos?.totalMes || 0) / 1000).toFixed(1)}K`}
              percentage={{
                color: "info",
                amount: `$${(kpis?.costos?.costoPromedioPorEntrega || 0).toFixed(2)}`,
                label: "por entrega",
              }}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="warning"
              icon="local_gas_station"
              title="Combustible"
              count={`$${((kpis?.costos?.combustible || 0) / 1000).toFixed(1)}K`}
              percentage={{
                color: "success",
                amount: `${(kpis?.costos?.totalMes || 0) > 0
                  ? (((kpis?.costos?.combustible || 0) / (kpis.costos.totalMes || 1)) * 100).toFixed(1)
                  : "0.0"
                  }%`,
                label: "del total",
              }}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="error"
              icon="build"
              title="Mantenimiento"
              count={`$${((kpis?.costos?.mantenimiento || 0) / 1000).toFixed(1)}K`}
              percentage={{
                color: "success",
                amount: `${(kpis?.costos?.totalMes || 0) > 0
                  ? (((kpis?.costos?.mantenimiento || 0) / (kpis.costos.totalMes || 1)) * 100).toFixed(1)
                  : "0.0"
                  }%`,
                label: "del total",
              }}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="success"
              icon="people"
              title="Salarios"
              count={`$${((kpis?.costos?.salarios || 0) / 1000).toFixed(1)}K`}
              percentage={{
                color: "success",
                amount: `${(kpis?.costos?.totalMes || 0) > 0
                  ? (((kpis?.costos?.salarios || 0) / (kpis.costos.totalMes || 1)) * 100).toFixed(1)
                  : "0.0"
                  }%`,
                label: "del total",
              }}
            />
          </MDBox>
        </Grid>
      </Grid>

      {/* Gráficas */}
      <Grid container spacing={3}>
        {/* Entregas por Día */}
        <Grid item xs={12} lg={6}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={3}>
                Entregas por Día
              </MDTypography>
              <MDBox height="300px">
                <VerticalBarChart chart={entregasPorDia} height="300px" />
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        {/* Vehículos por Tipo */}
        <Grid item xs={12} lg={6}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={3}>
                Distribución de Vehículos
              </MDTypography>
              <MDBox height="300px">
                <PieChart chart={vehiculosPorTipo} height="300px" />
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        {/* Eficiencia de Rutas */}
        <Grid item xs={12} lg={6}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={3}>
                Eficiencia por Ruta
              </MDTypography>
              <MDBox height="300px">
                <HorizontalBarChart chart={eficienciaRutas} height="300px" />
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        {/* Costos Mensuales */}
        <Grid item xs={12} lg={6}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={3}>
                Evolución de Costos
              </MDTypography>
              <MDBox height="300px">
                <DefaultLineChart chart={costosMensuales} height="300px" />
              </MDBox>
            </MDBox>
          </Card>
        </Grid>
      </Grid>

      {/* Indicadores Adicionales */}
      <Grid container spacing={3} mt={1}>
        <Grid item xs={12} md={4}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={2}>
                <Icon>schedule</Icon> Tiempo Promedio de Entrega
              </MDTypography>
              <MDTypography variant="h3" color="info" fontWeight="bold">
                2.3 hrs
              </MDTypography>
              <MDTypography variant="button" color="text">
                Objetivo: 2.5 hrs
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={2}>
                <Icon>speed</Icon> Distancia Total Recorrida
              </MDTypography>
              <MDTypography variant="h3" color="success" fontWeight="bold">
                12,456 km
              </MDTypography>
              <MDTypography variant="button" color="text">
                Este mes
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={2}>
                <Icon>star</Icon> Satisfacción del Cliente
              </MDTypography>
              <MDTypography variant="h3" color="warning" fontWeight="bold">
                4.7/5.0
              </MDTypography>
              <MDTypography variant="button" color="text">
                Basado en 892 calificaciones
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}
